import { parseDate, displayDate, displayDateTime } from './format.js';
import { applyMask, unmask, todayIso, parseTime, regexMaskOf, regexPrefixOk, regexFullOk, regexClip, parseDateTime, dateRangeError } from './editors.js';
import { NIR, CHECKISH, cycleCheck3, MAX_SCROLL_PX } from './grid-util.js';
import { byteLength, normValidation, parseMulti } from './input.js';
export class GridEdit {
    // ═══════════════════════ 편집 ═══════════════════════
    /** @internal */ positionIme() {
        const c = this.cols[this.focus.ci];
        const ime = this.ime;
        const nir = this.focus.vi === NIR;
        if (!c || (!this.disp.length && !nir)) {
            ime.style.left = '-9999px';
            return;
        }
        const main = this.bodyPanes[1];
        const rh = nir ? this.rh : this.rowH(this.focus.vi), vh = main.clientHeight;
        const total = this.totalH();
        const vTop = total > MAX_SCROLL_PX ? (main.scrollTop / Math.max(1, MAX_SCROLL_PX - vh)) * (total - vh) : main.scrollTop;
        const top = (nir ? this.pinBox[3].offsetTop : this.bodyPanes[1].parentElement.offsetTop + this.rowTop(this.focus.vi) - vTop) + (c.lh ? c.lt * this.lineH : 0), ch = c.lh ? c.lh * this.lineH : rh;
        const left = c.pane === 0 ? c.left : c.pane === 2 ? this.root.clientWidth - this.paneWidth[2] + c.left : this.paneWidth[0] + c.left - main.scrollLeft;
        ime.style.top = top + 'px';
        ime.style.left = left + 'px';
        ime.style.width = Math.max(c.width, this.editing?.kind === 'combo' ? 160 : 0) + 'px';
        ime.style.height = (this.editing?.kind === 'textarea' ? Math.max(ch, this.lineH * 4) : ch) + 'px';
        if (!this.drop.hidden) {
            this.drop.style.top = top + ch + 'px';
            this.drop.style.left = left + 'px';
        }
        if (!this.cal.hidden) {
            this.cal.style.top = top + ch + 'px';
            this.cal.style.left = left + 'px';
        }
        if (this.ced && this.cedWrap) {
            const w = this.cedWrap;
            w.style.top = top + 'px';
            w.style.left = left + 'px';
            w.style.minWidth = c.width + 'px';
            w.style.minHeight = ch + 'px';
        }
    }
    /** @internal */ beginEdit(mode) {
        if (this.editing)
            return true;
        let row = this.getFocusedRow();
        const c = this.cols[this.focus.ci], nir = this.focus.vi === NIR;
        // 편집 폼 방식: 셀 대신 폼 (바로 입력 글자는 그 칸에)
        if (!nir && this.formMode() && this.opts.editable && c && !c.special && row >= 0) {
            const init = mode === 'quick' && this.ime.value ? this.ime.value : undefined;
            this.ime.value = '';
            return this.showEditForm(row, { focusCol: c.id, initialText: init });
        }
        // 새 항목 행: 입력을 시작하면 INSERT 행을 만듦 (목록에는 확정 때 붙음)
        if (nir && row < 0) {
            if (!c || c.special || !this.editorOf(c) || this.columnAccess(c) !== 'edit')
                return false;
            row = this.ensureNirRow();
            if (row < 0)
                return false;
        }
        if (!c || row < 0 || !this.canEditCol(c, row))
            return false;
        const kind = this.editorOf(c, row);
        if (CHECKISH.has(kind))
            return false;
        // 행 단위 편집: 편집을 시작하면 그 행 전체가 편집 상태 (새 항목 행은 따로)
        if (this.rowEditOn() && !nir && (!this.rowEdit || this.rowEdit.row !== row)) {
            if (this.rowEdit && !this.saveRowEdit())
                return false;
            this.startRowEdit(row);
        }
        const cust = this.customEditorOf(c);
        if (cust) {
            const ok = this.openCustomEditor(cust, c, row, mode);
            if (ok)
                this.fireEditStart(row, c.id);
            return ok;
        }
        this.editing = { row, vi: this.focus.vi, ci: this.focus.ci, mode, kind };
        if (this.opts.showIndicator)
            this.bump();
        const ime = this.ime;
        const sm = typeof c.def.mask === 'string' ? c.def.mask : null, rm = regexMaskOf(c.def.mask);
        if (mode === 'full' && kind !== 'checkCombo') {
            const v = this.valueOf(c, row);
            ime.value = kind === 'combo' ? (v == null ? '' : this.comboMap(c).get(String(v))?.label ?? String(v))
                : kind === 'date' ? (c.def.dateKind === 'datetime' ? displayDateTime(v) : displayDate(v)) : v == null ? '' : sm ? applyMask(String(v), sm) : String(v);
            ime.setSelectionRange(0, ime.value.length);
        }
        this.reGood = mode === 'full' ? ime.value : '';
        ime.placeholder = rm?.placeholder ?? (kind === 'date' && c.def.dateKind === 'datetime' ? 'YYYY-MM-DD HH:mm' : '');
        ime.classList.add('og-editing');
        ime.classList.toggle('og-ta', kind === 'textarea');
        if (kind === 'date') {
            const cur = parseDate((ime.value || '').split(' ')[0], 'YYYY-MM-DD');
            this.calCur = this.clampDate(c, row, cur ?? todayIso());
            this.cal.hidden = false;
            this.renderCal();
        }
        ime.style.textAlign = kind === 'number' || kind === 'spin' ? 'right' : 'left';
        // 화면 키보드: 숫자는 숫자 자판, 날짜·시간은 숫자 자판(0930 → 09:30, 20261009 → 날짜)
        ime.inputMode = kind === 'number' || kind === 'spin' ? 'decimal' : kind === 'date' || kind === 'time' || (sm && /^[#\-./: ]+$/.test(sm)) ? 'numeric' : 'text';
        if (nir)
            this.bump();
        this.positionIme();
        if (kind === 'combo' && c.def.immediateDropDown !== false)
            this.openDrop(c, mode === 'full' ? '' : ime.value);
        if (kind === 'checkCombo')
            this.startCheckCombo(c, row, mode);
        this.byteGood = c.def.maxBytes && byteLength(ime.value, this.byteEnc()) <= c.def.maxBytes ? ime.value : '';
        if (c.def.maxBytes) {
            if (mode === 'quick' && !this.composing)
                this.checkBytesInput();
            else
                this.updateByteCount();
        }
        this.fireEditStart(row, c.id);
        return true;
    }
    /** @internal */ fireEditStart(row, col) { try {
        this.opts.onEditStart?.({ row, col, grid: this });
    }
    catch (err) {
        console.error(err);
    } }
    // ── 사용자 편집기 (cellEditor) ──
    /** @internal */ customEditorOf(c) {
        const e = c?.def.cellEditor;
        if (!e || c.special)
            return null;
        if (typeof e === 'object' && typeof e.open === 'function' && typeof e.close === 'function')
            return e;
        return this.opts.customEditor ?? null;
    }
    /** @internal */ openCustomEditor(ed, c, row, mode) {
        const initialText = mode === 'quick' && this.ime.value ? this.ime.value : null;
        this.ime.value = '';
        this.editing = { row, vi: this.focus.vi, ci: this.focus.ci, mode, kind: 'custom' };
        if (!this.cedWrap) {
            const w = document.createElement('div');
            w.className = 'og-ced';
            w.hidden = true;
            // 편집기 안 키는 편집기가 먼저(React 포털 수신기는 안쪽 host 에) → 처리 안 한 Enter·Tab·Esc 만 여기서
            w.addEventListener('keydown', e => this.onCustomKey(e));
            w.addEventListener('mousedown', e => e.stopPropagation());
            this.root.appendChild(w);
            this.cedWrap = w;
        }
        const w = this.cedWrap;
        w.textContent = '';
        const host = document.createElement('div');
        host.className = 'og-cedh';
        w.appendChild(host);
        w.hidden = false;
        const token = ++this.cedSeq;
        const st = { ed, pending: null, token };
        this.ced = st;
        this.positionIme();
        const live = () => this.ced?.token === token;
        const ctx = {
            value: this.valueOf(c, row), row, col: c.id, ds: this.ds, grid: this, column: c.def, initialText, mode,
            commit: (v, move = 'none') => {
                if (!live())
                    return false;
                st.pending = { v };
                if (!this.commitEdit())
                    return false;
                this.focusGrid();
                if (move === 'next')
                    this.moveAfterEnter(false);
                else if (move === 'down')
                    this.setFocus(this.focus.vi + 1, this.focus.ci, false);
                return true;
            },
            change: v => { if (live())
                st.pending = { v }; },
            cancel: () => { if (live()) {
                this.cancelEdit();
                this.focusGrid();
            } },
        };
        try {
            ed.open(host, ctx);
        }
        catch (err) {
            console.error(err);
            this.cancelEdit();
            return false;
        }
        // 편집기가 스스로 포커스를 잡지 않았으면 첫 입력 요소로
        if (live() && !host.contains(document.activeElement)) {
            const f = host.querySelector('[autofocus],input:not([disabled]),select,textarea,button:not([disabled]),[tabindex]:not([tabindex="-1"])');
            if (f)
                f.focus({ preventScroll: true });
            else {
                host.tabIndex = -1;
                host.focus({ preventScroll: true });
            }
        }
        if (this.opts.showIndicator)
            this.bump();
        return true;
    }
    /** @internal */ onCustomKey(e) {
        if (!this.ced || e.defaultPrevented || e.isComposing || e.keyCode === 229)
            return;
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.cancelEdit();
            this.focusGrid();
            return;
        }
        if (e.key === 'Enter' && !e.altKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            if (this.commitEdit()) {
                this.focusGrid();
                this.moveAfterEnter(e.shiftKey);
            }
            return;
        }
        if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            if (this.commitEdit()) {
                this.focusGrid();
                this.moveTab(e.shiftKey);
            }
        }
    }
    /** @internal */ closeCustom() {
        const st = this.ced;
        if (!st)
            return;
        this.ced = null;
        const w = this.cedWrap;
        const had = w.contains(document.activeElement);
        w.hidden = true;
        try {
            st.ed.close();
        }
        catch (err) {
            console.error(err);
        }
        if (had)
            this.ime.focus({ preventScroll: true });
    }
    /** 사용자 편집기가 열려 있나 */
    isCustomEditing() { return !!this.ced; }
    /** 숨은 입력칸에서 조합을 끝낸 뒤 여는 편집 (사용자 편집기·편집 폼 — 조합 중 포커스를 옮기면 IME 가 깨짐) */
    /** @internal */ opensAfterIme() { return !!this.customEditorOf(this.cols[this.focus.ci]) || (!!this.formMode() && this.focus.vi !== NIR && !!this.opts.editable); }
    /** @internal */ onCompositionStart() {
        this.composing = true;
        // 사용자 편집기 칸·편집 폼: 조합은 숨은 입력칸에서 끝내고(compositionend) 그 글자로 연다 — 조합 중 포커스를 옮기면 IME 가 깨짐
        if (!this.editing && this.opensAfterIme())
            return;
        if (!this.editing) {
            if (!this.beginEdit('quick') && !this.incEnabled())
                this.ime.value = '';
        }
    }
    /** @internal */ onCompositionEnd() {
        this.composing = false;
        if (!this.editing && this.ime.value && this.opensAfterIme()) {
            if (!this.beginEdit('quick'))
                this.ime.value = '';
            return;
        }
        if (!this.editing && this.incEnabled() && this.ime.value) {
            const t = this.ime.value;
            this.ime.value = '';
            this.incInput(t);
            return;
        }
        // 편집할 수 없는 칸에서 조합한 글자(증분 검색도 꺼짐)는 버림 — 숨은 입력칸에 남으면 다음 편집 칸 바로 입력에 붙음 ("이타이")
        if (!this.editing) {
            this.ime.value = '';
            this.pendingKey = null;
            return;
        }
        this.checkRegexInput(); // 정규식 마스크: 조합이 끝난 글자도 검사
        this.checkBytesInput(); // 바이트 제한: 조합이 끝난 음절 기준 (조합 중에는 자르지 않음)
        if (this.editing?.kind === 'checkCombo')
            this.filterCheckCombo(this.ime.value);
        else if (this.editing?.kind === 'combo') {
            if (this.cols[this.editing.ci].def.immediateDropDown !== false || !this.drop.hidden)
                this.openDrop(this.cols[this.editing.ci], this.ime.value);
        }
        else if (this.editing?.kind === 'text' && this.cols[this.editing.ci].def.autoComplete)
            this.openAuto(this.cols[this.editing.ci], this.ime.value);
        if (this.pendingKey) {
            const k = this.pendingKey;
            this.pendingKey = null;
            setTimeout(() => this.handleEditKey(k), 0);
        }
    }
    /** @internal */ onImeInput() {
        if (!this.editing) {
            if (this.composing)
                return;
            if (!this.ime.value)
                return;
            if (!this.beginEdit('quick')) {
                const t = this.ime.value;
                this.ime.value = '';
                if (this.incEnabled())
                    this.incInput(t);
                return;
            }
        }
        if (this.editing?.kind === 'checkCombo') {
            this.filterCheckCombo(this.ime.value);
            return;
        }
        if (this.editing?.kind === 'combo') {
            if (this.cols[this.editing.ci].def.immediateDropDown !== false || !this.drop.hidden)
                this.openDrop(this.cols[this.editing.ci], this.ime.value);
        }
        else if (this.editing?.kind === 'text' && this.cols[this.editing.ci].def.autoComplete)
            this.openAuto(this.cols[this.editing.ci], this.ime.value);
        else if (this.editing?.kind === 'time' && !this.composing) {
            const v = this.ime.value;
            if (/^\d{4}$/.test(v)) {
                const t = parseTime(v);
                if (t)
                    this.ime.value = t;
            }
            else if (/[^\d:]/.test(v))
                this.ime.value = v.replace(/[^\d:]/g, '');
        }
        else if (this.editing?.kind === 'number' || this.editing?.kind === 'spin') {
            const v = this.ime.value;
            if (/[^\d.,\-]/.test(v) && !this.composing)
                this.ime.value = v.replace(/[^\d.,\-]/g, '');
        }
        const c = this.editing && this.cols[this.editing.ci];
        if (c?.def.maxLength && this.ime.value.length > c.def.maxLength)
            this.ime.value = this.ime.value.slice(0, c.def.maxLength);
        if (c && typeof c.def.mask === 'string' && !this.composing) {
            const m = applyMask(this.ime.value, c.def.mask);
            if (m !== this.ime.value)
                this.ime.value = m;
        }
        if (!this.composing)
            this.checkRegexInput();
        if (c?.def.maxBytes) {
            if (!this.composing)
                this.checkBytesInput();
            else
                this.updateByteCount();
        }
        if (this.editing?.kind === 'date' && !this.cal.hidden) {
            const d = parseDate(this.ime.value.split(' ')[0], 'YYYY-MM-DD');
            if (d && d !== this.calCur) {
                this.calCur = d;
                this.renderCal();
            }
        }
    }
    /** @internal */ checkRegexInput() {
        const ed = this.editing;
        if (!ed || ed.kind === 'custom')
            return;
        const d = this.cols[ed.ci]?.def, m = regexMaskOf(d?.mask);
        if (!m)
            return;
        // upper 컬럼은 대문자로 바꿔 검사 (소문자로 쳐도 됨)
        if (d?.upper && this.ime.value !== this.ime.value.toUpperCase()) {
            const st = this.ime.selectionStart;
            this.ime.value = this.ime.value.toUpperCase();
            this.ime.setSelectionRange(st, st);
        }
        const v = this.ime.value;
        if (regexPrefixOk(m, v)) {
            this.reGood = v;
            if (this.ime.classList.contains('og-bad')) {
                this.ime.classList.remove('og-bad');
                this.hideTip();
            }
            return;
        }
        // 붙여넣기 등 여러 글자: 앞에서부터 맞는 데까지, 한 글자면 되돌림
        const keep = v.startsWith(this.reGood) || v.length > this.reGood.length + 1 ? regexClip(m, v) : this.reGood;
        this.ime.value = keep;
        this.reGood = keep;
        this.ime.setSelectionRange(keep.length, keep.length);
        this.showTip(m.message ?? (this.t('입력 형식이 맞지 않습니다') + (m.placeholder ? ` (${m.placeholder})` : '')));
    }
    /** 편집 확정. 실패(검증 오류)면 false 를 돌려주고 편집기를 유지한다 */
    commitEdit() {
        const ed = this.editing;
        if (!ed)
            return true;
        const c = this.cols[ed.ci];
        if (ed.kind === 'custom') {
            // 사용자 편집기: change/commit 으로 받은 값 (없으면 바꾸지 않고 닫음)
            const p = this.ced?.pending;
            const vr = p ? normValidation(c.def.validate?.(p.v, ed.row, this.dsFor(ed.row))) : null;
            if (vr?.level === 'error') {
                this.showTip(vr.message);
                return false;
            }
            const be = p ? this.bytesError(c, p.v) : null;
            if (be) {
                this.showTip(be);
                return false;
            }
            this.endEdit(true);
            if (p && !Object.is(p.v, this.valueOf(c, ed.row)))
                this.setCellByUser([{ row: ed.row, col: c.id, value: p.v }]);
            if (p)
                this.afterCellValidate(ed.row, c.id, vr);
            return true;
        }
        const text = this.ime.value;
        const parsed = ed.kind === 'checkCombo' ? { value: this.checkComboValue() } : this.parseInput(c, text, ed.kind);
        if ('error' in parsed) {
            this.showTip(parsed.error);
            this.ime.classList.add('og-bad');
            return false;
        }
        // 검증 수준: 오류만 막음, 경고·안내는 저장하고 칸에 표시 (15차)
        const vr = normValidation(c.def.validate?.(parsed.value, ed.row, this.dsFor(ed.row)));
        if (vr?.level === 'error') {
            this.showTip(vr.message);
            this.ime.classList.add('og-bad');
            return false;
        }
        this.endEdit(true);
        this.setCellByUser([{ row: ed.row, col: c.id, value: parsed.value }]);
        this.afterCellValidate(ed.row, c.id, vr);
        return true;
    }
    /** 확정 뒤 경고·안내 표시 (값이 실제로 들어갔을 때 — onBeforeChange 가 취소했으면 그대로) */
    /** @internal */ afterCellValidate(row, col, vr) {
        const k = row + ':' + col;
        if (!vr) {
            if (this.warns.delete(k))
                this.bump();
            return;
        }
        if (vr.level === 'error')
            return;
        this.warns.set(k, vr);
        this.bump();
        this.showTip(vr.message, vr.level);
        setTimeout(() => this.hideTip(), 2500);
    }
    cancelEdit() { if (this.editing)
        this.endEdit(); }
    /** @internal */ endEdit(committed = false) {
        const ed = this.editing;
        const wasNir = ed?.vi === NIR;
        this.editing = null;
        this.ccSel = null;
        if (this.byteEl && !this.byteEl.hidden)
            this.byteEl.hidden = true;
        if (this.ced)
            this.closeCustom();
        this.ime.value = '';
        if (this.ime.placeholder)
            this.ime.placeholder = '';
        if (wasNir)
            this.requestRender(true);
        this.ime.classList.remove('og-editing', 'og-bad', 'og-ta');
        this.drop.hidden = true;
        this.cal.hidden = true;
        this.hideTip();
        this.bump();
        if (ed) {
            const col = this.cols[ed.ci]?.id ?? '';
            try {
                this.opts.onEditEnd?.({ row: ed.row, col, committed, grid: this });
            }
            catch (err) {
                console.error(err);
            }
        }
    }
    /** @internal */ commitEditOnScroll() { if (this.editing && !this.commitEdit())
        this.cancelEdit(); }
    /** @internal */ parseInput(c, text, kind, row = this.editing?.row ?? -1) {
        if (c.def.valueParser) {
            const v = c.def.valueParser({ text, row, col: c.id, ds: this.ds, oldValue: row >= 0 ? this.valueOf(c, row) : null });
            return v != null && typeof v === 'object' && 'error' in v ? { error: String(v.error) } : { value: v };
        }
        if (kind === 'text' && c.def.autoComplete && !this.drop.hidden && this.dropIdx >= 0 && this.dropItems[this.dropIdx])
            text = this.dropItems[this.dropIdx].value;
        const t = text.trim();
        if (kind === 'number' || kind === 'spin') {
            if (!t)
                return { value: null };
            const n = Number(t.replace(/,/g, ''));
            if (isNaN(n))
                return { error: this.t('숫자를 입력하세요') };
            if (c.def.min != null && n < c.def.min)
                return { error: `${c.def.min} 이상이어야 합니다` };
            if (c.def.max != null && n > c.def.max)
                return { error: `${c.def.max} 이하여야 합니다` };
            return { value: n };
        }
        if (kind === 'date') {
            if (!t)
                return { value: null };
            const dt = c.def.dateKind === 'datetime';
            const d = dt ? parseDateTime(t, c.def.dateFormat === 'YYYYMMDD' ? 'YYYYMMDD' : 'YYYY-MM-DD') : parseDate(t, c.def.dateFormat);
            if (!d)
                return { error: dt ? this.t('날짜·시간 형식이 아닙니다 (예: 20261009 0930)') : this.t('날짜 형식이 아닙니다 (예: 20261009)') };
            // 범위 검사는 바꾼 값만 (예전에 들어간 범위 밖 값을 그대로 확정하는 것은 막지 않음)
            const lo = this.dateBound(c, row, 'min'), hi = this.dateBound(c, row, 'max'), re = row >= 0 && d === this.ds.get(row, c.id) ? null : dateRangeError(d, lo, hi);
            if (re === 'min')
                return { error: this.t('{d} 이후 날짜만 입력할 수 있습니다', { d: displayDate(lo) }) };
            if (re === 'max')
                return { error: this.t('{d} 이전 날짜만 입력할 수 있습니다', { d: displayDate(hi) }) };
            return { value: d };
        }
        if (kind === 'time') {
            if (!t)
                return { value: null };
            const v = parseTime(t);
            return v ? { value: v } : { error: this.t('시간 형식이 아닙니다 (예: 0930, 9:30)') };
        }
        if (kind === 'checkCombo') {
            const r = parseMulti(text, this.itemsOf(c, row), { sep: c.def.valueSeparator ?? ',', dispSep: c.def.displaySeparator ?? ', ', maxSelect: c.def.maxSelect });
            return 'error' in r ? { error: r.bad ? this.t('목록에 없는 값입니다') + ` (${r.bad})` : this.t('최대 {n}개까지 고를 수 있습니다', { n: c.def.maxSelect ?? 0 }) } : r;
        }
        if (kind === 'combo') {
            if (!t)
                return { value: null };
            if (!this.drop.hidden && this.dropItems[this.dropIdx])
                return { value: this.dropItems[this.dropIdx].value };
            const items = this.itemsOf(c, this.editing?.row ?? row), q = t.toLowerCase();
            const exact = items.find(i => i.value.toLowerCase() === q || i.label.toLowerCase() === q);
            // editList: 목록에 없는 글자도 그대로 (DropDownListStyle lsEditList)
            return exact ? { value: exact.value } : c.def.listStyle === 'editList' ? { value: c.def.upper ? t.toUpperCase() : t } : { error: this.t('목록에 없는 값입니다') };
        }
        let v = c.def.upper ? t.toUpperCase() : text;
        const rm = regexMaskOf(c.def.mask);
        if (rm) {
            if (!regexFullOk(rm, v))
                return { error: rm.message ?? (this.t('입력 형식이 맞지 않습니다') + (rm.placeholder ? ` (${rm.placeholder})` : '')) };
        }
        else if (typeof c.def.mask === 'string')
            v = unmask(v, c.def.mask);
        if (c.def.maxLength)
            v = v.slice(0, c.def.maxLength);
        if (c.def.maxBytes && byteLength(v, this.byteEnc()) > c.def.maxBytes)
            return { error: this.bytesError(c, v) };
        return { value: v === '' ? null : v };
    }
    /**
     * 사용자 입력(편집·붙여넣기·체크·지우기) 반영 + 실행취소 기록 + onCellChange — 그리드가 값을 쓰는 경로는 모두 여기로 (15차: 쓰기 전 이벤트·바이트 제한·행 편집 버퍼)
     * source = 경로 (onBeforeChange 의 source). opt.noBefore = 이미 거침(행 편집 저장), noBuffer = 행 편집 버퍼를 거치지 않음
     */
    /** @internal */ setCellByUser(changes, label = this.t('편집'), source = 'edit', opt = {}) {
        // 행 단위 편집: 한 행의 편집·체크는 버퍼로 (Dataset 은 저장 때)
        if (!opt.noBuffer && (source === 'edit' || source === 'check') && changes.length && this.rowEditOn()) {
            const row = changes[0].row;
            if (row !== this.nirRow && changes.every(c => c.row === row)) {
                this.bufferRowEdit(changes, source);
                return;
            }
        }
        // 다른 경로(붙여넣기·채우기 등)가 편집 중인 행을 건드리면 버퍼를 먼저 반영
        if (this.rowEdit && !this.rowEdit.saving && changes.some(c => c.row === this.rowEdit.row))
            this.saveRowEdit(false);
        if (!opt.noBefore)
            changes = this.beforeChanges(changes, source);
        if (!changes.length)
            return;
        const undo = [];
        const fired = [];
        this.ds.batch(() => {
            for (const ch of changes) {
                const setter = this.colInfo(ch.col)?.def.valueSetter;
                if (setter) {
                    // valueSetter: 행 전체를 앞뒤로 비교해 바뀐 셀을 실행 취소에 담음
                    const ids = this.ds.columns.map(x => x.id), before = ids.map(id => this.ds.get(ch.row, id));
                    const oldV = this.colInfo(ch.col) ? this.valueOf(this.colInfo(ch.col), ch.row) : null;
                    if (setter({ row: ch.row, ds: this.ds, col: ch.col, value: ch.value, oldValue: oldV }) === false)
                        continue;
                    ids.forEach((id, i) => { const nv = this.ds.get(ch.row, id); if (nv !== before[i])
                        undo.push({ row: ch.row, col: id, old: before[i] }); });
                    this.errors.delete(ch.row + ':' + ch.col);
                    if (this.warns.size)
                        this.warns.delete(ch.row + ':' + ch.col);
                    fired.push({ row: ch.row, col: ch.col, value: ch.value, old: oldV, grid: this });
                    continue;
                }
                const old = this.ds.get(ch.row, ch.col);
                if (this.ds.set(ch.row, ch.col, ch.value)) {
                    undo.push({ row: ch.row, col: ch.col, old });
                    this.errors.delete(ch.row + ':' + ch.col);
                    if (this.warns.size)
                        this.warns.delete(ch.row + ':' + ch.col);
                    fired.push({ row: ch.row, col: ch.col, value: this.ds.get(ch.row, ch.col), old, grid: this });
                }
            }
        });
        this.pushUndo({ label: label === '편집' && undo.length === 1 ? `편집 ${this.cols.find(c => c.id === undo[0].col)?.def.header ?? undo[0].col}` : label, at: Date.now(), cells: undo, rows: [] });
        for (const e of fired)
            this.scheduleAsync(e.row, e.col);
        for (const e of fired)
            this.opts.onCellChange?.(e);
        this.bump();
    }
    /** 선택한 셀(여러 범위 포함) 지우기 — 편집 가능한 칸만, 실행취소 한 번 */
    /** @internal */ clearRange() {
        const ch = [], seen = new Set();
        for (const { r0, r1, c0, c1 } of this.selRanges())
            for (let vi = r0; vi <= r1; vi++) {
                const row = this.disp[vi];
                if (row == null || row < 0)
                    continue;
                for (let ci = c0; ci <= c1; ci++) {
                    const c = this.cols[ci];
                    const k = row + ':' + ci;
                    if (!seen.has(k) && this.canEditCol(c, row)) {
                        seen.add(k);
                        ch.push({ row, col: c.id, value: null });
                    }
                }
            }
        if (ch.length)
            this.setCellByUser(ch, this.t('지우기'), 'clear');
    }
    /** @internal */ toggleCheckCell(c, row) {
        const [on, off] = this.checkValues(c), kind = this.editorOf(c, row), cur = this.ds.get(row, c.id);
        if (kind === 'check3') {
            this.setCellByUser([{ row, col: c.id, value: cycleCheck3(cur, this.checkValues3(c)) }], this.t('체크'), 'check');
            return;
        }
        if (kind !== 'radio') {
            this.setCellByUser([{ row, col: c.id, value: this.isChecked(c, cur) ? off : on }], this.t('체크'), 'check');
            return;
        }
        // 라디오: 켜기만 (이미 켜져 있으면 그대로). 같은 그룹(행 안)·같은 컬럼(radioColumn)의 다른 칸은 끔
        if (this.isChecked(c, cur))
            return;
        const ch = [{ row, col: c.id, value: on }];
        const g = c.def.radioGroup;
        if (g)
            for (const o of this.cols)
                if (o !== c && o.def.radioGroup === g && this.isChecked(o, this.ds.get(row, o.id)) && this.canEditCol(o, row))
                    ch.push({ row, col: o.id, value: this.checkValues(o)[1] });
        if (c.def.radioColumn)
            for (let r = 0; r < this.ds.length; r++)
                if (r !== row && this.ds.isVisibleRow(r) && this.isChecked(c, this.ds.get(r, c.id)))
                    ch.push({ row: r, col: c.id, value: off });
        this.setCellByUser(ch, this.t('선택'), 'check');
    }
    /** 라디오 그룹(행 안)에서 켜진 컬럼 id, 없으면 null */
    getRadioValue(row, group) {
        for (const c of this.cols)
            if (c.def.radioGroup === group && this.isChecked(c, this.ds.get(row, c.id)))
                return c.id;
        return null;
    }
    /** @internal */ toggleCheckRow(row) {
        if (this.opts.tree?.checkCascade)
            this.cascadeCheck(row, !this.checked.has(row));
        else
            this.checked.has(row) ? this.checked.delete(row) : this.checked.add(row);
        this.renderHeader();
        this.bump();
        this.opts.onCheckChange?.({ rows: this.getCheckedRows(), grid: this });
    }
}
